/**
 * 将派件APP需要的状态和操作集中管理, 方便无网精准控制状态读取和操作
 */
import { create } from 'zustand';
import { DeliveryTaskOrderType } from '../graphql/types/DeliveryTaskOrderType';
import { useEffect, useState, useCallback } from 'react';
import { immer } from 'zustand/middleware/immer';
import { DeliveryTaskOrderListResponse, GET_DELIVERY_TASK_ORDER_LIST, GET_NEW_DELIVERY_TASK_ORDER_LIST, NewDeliveryTaskOrderListResponse } from '../graphql/queries/deliveryTaskOrderList';
import { GET_DELIVERY_POD_ABNORMAL_TYPE_LIST, GetDeliveryPODAbnormalTypeListResponse } from '../graphql/queries/getDeliveryPODAbnormalTypeList';
import { client } from '../../../graphql/client';
import { DeliveredEventInput, handleDeliveredEvent } from '../graphql/mutations/delivered';
import { handleNewAssignmentOutForDeliveryEvent, OutForDeliveryInput } from '../graphql/mutations/newAssignmentOutForDelivery';
import { DeliveryFailedEventInput, handleDeliveryFailedEvent } from '../graphql/mutations/deliveredFailed';
import { handleNewAssignmentDeliveryScanEvent, NewAssignmentScanInput } from '../graphql/mutations/newAssignmentDeliveryScan';
import { handleTransferInDeliveryScanEvent, TransferInScanInput } from '../graphql/mutations/transferInDeliveryScan';
import { handleTransferInOutForDeliveryEvent } from '../graphql/mutations/transferInOutForDelivery';
import { ExceptionDictionaryType, getExceptionDictionaryForDelivery } from '../graphql/queries/getExceptionDictionaryForDelivery';
import { getExceptionDictionaryForDsp } from '../graphql/queries/getExceptionDictionaryForDsp';
import { DeliveryMethodI18nItemType, getDeliveryMethodI18nConfig } from '../../../graphql/queries/getDeliveryMethodI18nConfig';
import { isDspManager } from '../../../utils/auth';
import { OrderWithAddressType } from '../graphql/types/DeliveryTaskOrderType';
import {handleSubmitWhsReview} from "../graphql/mutations/submitWhsReview";

export interface DeliveryTaskOrderOperations {
    handleDelivered: (input: DeliveredEventInput, optimisticSubmission: boolean) => Promise<DeliveryTaskOrderType | undefined>;
    handleDeliveryFailed: (input: DeliveryFailedEventInput, optimisticSubmission: boolean) => Promise<DeliveryTaskOrderType | undefined>;
    handleNewAssignmentScan: (input: NewAssignmentScanInput) => Promise<DeliveryTaskOrderType | undefined>;
    handleTransferInScan: (input: TransferInScanInput) => Promise<DeliveryTaskOrderType | undefined>;
    handleNewAssignmentOutForDelivery: (input: OutForDeliveryInput, operatorName?: string | null) => Promise<boolean>;
    handleTransferInOutForDelivery: (input: OutForDeliveryInput) => Promise<boolean>;
    handleSubmitWhsReview: (operatorName?: string | null) => Promise<boolean>;
}

interface DeliveryStoreImpl {
    version: number;
    deliveryTaskOrderList: DeliveryTaskOrderType[];
    podAbnormalOrderList: DeliveryTaskOrderType[];
    loading: boolean;
    _isNeedRefresh: boolean;
    _isRetryBlocked: boolean;
    exceptionDictionaries: ExceptionDictionaryType[];
    // 妥投方式中英西文案配置
    deliveryMethodI18nConfig: DeliveryMethodI18nItemType[];
    fromMapMode: boolean; // 简化为布尔值
    // 配送任务颜色映射，内存持久（不做本地存储）
    routeColorMap: Record<string, string>;
    // 获取或分配某个配送任务的颜色
    getOrAssignRouteColor: (deliveryTaskId: string, palette: string[]) => string;
    // 临时存储：批量扫描的图片数据
    tempScanPhotos: Map<string, string>; // trackingNumber -> base64
    // 设置临时扫描图片
    setTempScanPhotos: (photos: Map<string, string>) => void;
    // 获取临时扫描图片
    getTempScanPhotos: () => Map<string, string>;
    // 清空临时扫描图片（完成后调用）
    clearTempScanPhotos: () => void;
    fetchExceptionDictionaries: () => Promise<void>;
    fetchDeliveryMethodI18nConfig: () => Promise<void>;
    setDeliveryTaskOrderList: (deliveryTaskOrderList: DeliveryTaskOrderType[]) => void;
    fetchDeliveryTaskOrderList: () => Promise<{deliveryTaskOrderList: DeliveryTaskOrderType[], podAbnormalOrderList: DeliveryTaskOrderType[]}>;
    _fetchDeliveryTaskOrderList: () => Promise<{deliveryTaskOrderList: DeliveryTaskOrderType[], podAbnormalOrderList: DeliveryTaskOrderType[]}>;
    _refreshPodAbnormalOrderListOnly: () => Promise<DeliveryTaskOrderType[]>;
    refreshDeliveryTaskOrderList: () => Promise<{deliveryTaskOrderList: DeliveryTaskOrderType[], podAbnormalOrderList: DeliveryTaskOrderType[]}>;
    isNeedRefresh: () => boolean;
    setNeedRefresh: (isNeedRefresh: boolean) => void;
    setRetryBlocked: (isBlocked: boolean) => void; // 设置重试阻塞状态
    isRetryBlocked: () => boolean; // 检查是否被重试阻塞
    setFromMapMode: (fromMapMode: boolean) => void; // 简化的设置方法
    handleDelivered: (input: DeliveredEventInput, optimisticSubmission: boolean) => Promise<DeliveryTaskOrderType | undefined>;
    handleNewAssignmentOutForDelivery: (input: OutForDeliveryInput, operatorName?: string | null) => Promise<boolean>;
    handleDeliveryFailed: (input: DeliveryFailedEventInput, optimisticSubmission: boolean) => Promise<DeliveryTaskOrderType | undefined>;
    handleNewAssignmentScan: (input: NewAssignmentScanInput) => Promise<DeliveryTaskOrderType | undefined>;
    handleTransferInScan: (input: TransferInScanInput) => Promise<DeliveryTaskOrderType | undefined>;
    handleTransferInOutForDelivery: (input: OutForDeliveryInput) => Promise<boolean>;
    handleSubmitWhsReview: (operatorName?: string | null) => Promise<boolean>;
    _updateDeliveryTaskOrder: (deliveryTaskOrder: Partial<DeliveryTaskOrderType>) => void;
    _removeDeliveryTaskOrder: (id: number) => void; // ✅ 新增：从 store 中删除订单
}

// export仅用于提供给native调用
export const useDeliveryStore = create<DeliveryStoreImpl>()(
    immer((set, get) => ({
        version: 0,
        deliveryTaskOrderList: [],
        podAbnormalOrderList: [],
        loading: false,
        _isNeedRefresh: false,
        _isRetryBlocked: false, // 初始化重试阻塞状态
        exceptionDictionaries: [],
        deliveryMethodI18nConfig: [],
        fromMapMode: false, // 初始化 fromMapMode
        routeColorMap: {},
        tempScanPhotos: new Map(),
        getOrAssignRouteColor: (deliveryTaskId: string, palette: string[]) => {
            // 如果已有颜色，直接返回
            const existing = get().routeColorMap[deliveryTaskId];
            if (existing) return existing;

            // 选择第一个未被使用的颜色；如果都被使用，则按数量取模复用
            const usedColors = new Set(Object.values(get().routeColorMap));
            let chosen = palette.find(color => !usedColors.has(color));
            if (!chosen) {
                chosen = '#cf179eff';
            }

            set(state => {
                state.routeColorMap[deliveryTaskId] = chosen as string;
            });
            return chosen as string;
        },
        setNeedRefresh: (isNeedRefresh: boolean) => {
            set({ _isNeedRefresh: isNeedRefresh, version: get().version + 1 });
        },
        setRetryBlocked: (isBlocked: boolean) => {
            set({ _isRetryBlocked: isBlocked });
        },
        isRetryBlocked: () => {
            return get()._isRetryBlocked;
        },
        setDeliveryTaskOrderList: (deliveryTaskOrderList) => {
            set({ deliveryTaskOrderList });
        },
        fetchDeliveryTaskOrderList: async () => {
            // 如果被重试阻塞，直接返回当前数据，不进行网络请求
            if (get().isRetryBlocked()) {
                console.log('Data fetch blocked due to retry in progress');
                return {
                    deliveryTaskOrderList: get().deliveryTaskOrderList,
                    podAbnormalOrderList: get().podAbnormalOrderList
                };
            }

            if (!get().isNeedRefresh()) {
                return {
                    deliveryTaskOrderList: get().deliveryTaskOrderList,
                    podAbnormalOrderList: get().podAbnormalOrderList
                };
            }
            return get()._fetchDeliveryTaskOrderList();
        },
        _fetchDeliveryTaskOrderList: async () => {
            if (get().version === 0) {
                set({ loading: true });
            }
            try {
                const { data } = await client.query<DeliveryTaskOrderListResponse>({
                    query: GET_DELIVERY_TASK_ORDER_LIST,
                    variables: {
                        maxDeliveryTaskId: get().version,
                    },
                });
                const maxDeliveryTaskId = data?.getDeliveryTaskOrderList?.maxDeliveryTaskId || 0;

                // 修复版本检查逻辑：如果版本不匹配或者当前数据为空，都应该更新数据
                const shouldUpdate = maxDeliveryTaskId != get().version || 
                                   get().podAbnormalOrderList.length === 0;
                
                if (shouldUpdate) {
                    set({
                        version: Number(maxDeliveryTaskId),
                        deliveryTaskOrderList: data?.getDeliveryTaskOrderList?.deliveryTaskOrderList || [],
                        podAbnormalOrderList: data?.getDeliveryTaskOrderList?.podAbnormalOrderList || []
                    });
                }
                return {
                    deliveryTaskOrderList: get().deliveryTaskOrderList,
                    podAbnormalOrderList: get().podAbnormalOrderList
                };
            } catch (error) {
                console.error('Error fetching delivery task order list:', error);
                return {
                    deliveryTaskOrderList: get().deliveryTaskOrderList,
                    podAbnormalOrderList: get().podAbnormalOrderList
                };
            } finally {
                set({ loading: false});
                get().setNeedRefresh(false);
            }
        },
        _refreshPodAbnormalOrderListOnly: async () => {
            try {
                const { data } = await client.query<GetDeliveryPODAbnormalTypeListResponse>({
                    query: GET_DELIVERY_POD_ABNORMAL_TYPE_LIST,
                });
                // 把接口返回写回状态
                set({ podAbnormalOrderList: data.getDeliveryPODAbnormalTypeListForCurrentUser || [] });
                return get().podAbnormalOrderList;
            } catch (e) {
                console.warn('仅刷新异常单失败', e);
                return get().podAbnormalOrderList;
            }
        },
        refreshDeliveryTaskOrderList: async () => {
            // 如果被重试阻塞，只更新 podAbnormalOrderList，不更新 deliveryTaskOrderList
            if (get().isRetryBlocked()) {
                console.log('Data refresh blocked due to retry in progress, only updating podAbnormalOrderList');
                const podAbnormalOrderList = await get()._refreshPodAbnormalOrderListOnly();
                return {
                    deliveryTaskOrderList: get().deliveryTaskOrderList,
                    podAbnormalOrderList
                };
            }

            get().setNeedRefresh(true);
            return get()._fetchDeliveryTaskOrderList();
        },
        // 判断是否需要重新获取订单列表
        // 可以增加刷新时间，比如10分钟
        isNeedRefresh: () => {
            const state = get();
            return state.deliveryTaskOrderList.length === 0 ||
                   state._isNeedRefresh;
        },
        _updateDeliveryTaskOrder: (partialDeliveryTaskOrder: Partial<DeliveryTaskOrderType>) => {
            const mergeDeliveryTaskOrder = (oldDeliveryTaskOrder: DeliveryTaskOrderType, partialDeliveryTaskOrder: Partial<DeliveryTaskOrderType>) => {
                const mergeOrderWithAddress = (oldOrderWithAddress: OrderWithAddressType, newOrderWithAddress?: OrderWithAddressType) => {
                    if (!newOrderWithAddress) {
                        return oldOrderWithAddress;
                    }
                    if (!oldOrderWithAddress) {
                        return newOrderWithAddress;
                    }
                    const newOrder = { ...oldOrderWithAddress.order, ...newOrderWithAddress.order };
                    return { ...oldOrderWithAddress, order: newOrder };
                };
                const newOrderWithAddress = mergeOrderWithAddress(oldDeliveryTaskOrder.orderWithAddress, partialDeliveryTaskOrder.orderWithAddress);
                return { ...oldDeliveryTaskOrder, ...partialDeliveryTaskOrder, orderWithAddress: newOrderWithAddress };
            };
            set(state => {
                const index = state.deliveryTaskOrderList.findIndex(item => item.id == partialDeliveryTaskOrder.id);
                if (index !== -1) {
                    const oldDeliveryTaskOrder = state.deliveryTaskOrderList[index];
                    const newDeliveryTaskOrder = mergeDeliveryTaskOrder(oldDeliveryTaskOrder, partialDeliveryTaskOrder);
                    state.deliveryTaskOrderList[index] = newDeliveryTaskOrder;
                } else {
                    state.deliveryTaskOrderList.push(partialDeliveryTaskOrder as DeliveryTaskOrderType);
                }
            });
        },
        _removeDeliveryTaskOrder: (id: number) => {
            set((state) => {
                // 🔧 修复: 使用宽松相等 == 而不是严格相等 ===，避免 number/string 类型不匹配
                const index = state.deliveryTaskOrderList.findIndex(item => item.id == id);
                if (index !== -1) {
                    const removedOrder = state.deliveryTaskOrderList[index];
                    state.deliveryTaskOrderList.splice(index, 1);
                    console.log(`✅ [set内部] 订单 ${id} (${removedOrder.trackingNumber}) 已删除, 剩余 ${state.deliveryTaskOrderList.length} 单`);
                } else {
                    console.warn(`⚠️ [set内部] 订单 ${id} 未找到!`);
                }
            });
        },
        fetchExceptionDictionaries: async () => {
            if (get().exceptionDictionaries.length > 0) {
                return;
            }
            try {
                // 根据角色选择不同的接口
                const isDsp = isDspManager();
                const exceptionDictionaries = isDsp
                    ? await getExceptionDictionaryForDsp()
                    : await getExceptionDictionaryForDelivery();
                set({ exceptionDictionaries });
            } catch (error) {
                console.error('Error fetching exception dictionaries:', error);
            }
        },
        fetchDeliveryMethodI18nConfig: async () => {
            if (get().deliveryMethodI18nConfig.length > 0) {
                return;
            }
            try {
                const list = await getDeliveryMethodI18nConfig();
                set({ deliveryMethodI18nConfig: list });
            } catch (error) {
                console.error('Error fetching delivery method i18n config:', error);
            }
        },
        handleDelivered: async (input: DeliveredEventInput, optimisticSubmission: boolean) => {
            if (optimisticSubmission === true){
                // ✅ 新逻辑：乐观提交时直接从 store 中删除订单
                console.log('乐观提交: 删除订单', input.id);
                get()._removeDeliveryTaskOrder(input.id);
            } else {
                // ✅ 新逻辑：自动上传时不更新 store，因为订单已经被删除了
                try {
                    console.debug('handleDelivered begin:', input);
                    const result = await handleDeliveredEvent(input);
                    console.debug('handleDelivered result (不更新 store):', result);
                    return result;
                } catch (error: any) {
                    console.error('handleDelivered catch error:', error);
                    if (error.networkError) {
                        console.debug('handleDelivered networkError (订单已删除，等待重试)', error);
                    }
                    throw error;
                }
            }
        },
        handleDeliveryFailed: async (input: DeliveryFailedEventInput, optimisticSubmission: boolean) => {
            const deliveryTaskOrder = get().deliveryTaskOrderList.find(item => item.id == input.id);
            if (!deliveryTaskOrder) {
                console.error('handleDeliveryFailed deliveryTaskOrder not found:', input);
                return;
            }
            console.log("dddddd")
            const setDeliveryTaskOrderToFailed = () => {
                const orderWithAddress = {
                    ...deliveryTaskOrder.orderWithAddress,
                    order: {
                        ...deliveryTaskOrder.orderWithAddress.order,
                        exceptionReasonCode: input.exceptionReasonCode,
                        deliveryAttempts: (deliveryTaskOrder.orderWithAddress.order.deliveryAttempts || 0) + 1
                    }
                };
                const partialDeliveryTaskOrder: Partial<DeliveryTaskOrderType> = {
                    id: input.id,
                    trackingNumber: input.trackingNumber,
                    isAbnormal: true,
                    orderWithAddress,
                };
                get()._updateDeliveryTaskOrder(partialDeliveryTaskOrder);
            };
            if (optimisticSubmission === true){
                console.log('乐观提交:');
                setDeliveryTaskOrderToFailed();
            }else {
                try {
                    const result = await handleDeliveryFailedEvent(input);
                    //不再更新store因乐观提交时已更新
                    return result;
                } catch (error: any) {
                    console.log('handleDeliveryFailed catch error:', error);
                    if (error.networkError) {
                        setDeliveryTaskOrderToFailed();
                    }
                    throw error;
                }
            }
        },
        handleNewAssignmentScan: async (input: NewAssignmentScanInput): Promise<DeliveryTaskOrderType | undefined> => {
            const result = await handleNewAssignmentDeliveryScanEvent(input);
            if (result) {
                get().setNeedRefresh(true);
            }
            console.log('handleNewAssignmentScan result:', result);
            return result;
        },
        handleTransferInScan: async (input: TransferInScanInput): Promise<DeliveryTaskOrderType | undefined> => {
            const result = await handleTransferInDeliveryScanEvent(input);
            if (result) {
                get().setNeedRefresh(true);
            }
            console.log('handleTransferInScan result:', result);
            return result;
        },
        handleNewAssignmentOutForDelivery: async (input: OutForDeliveryInput, operatorName?: string | null): Promise<boolean> => {
            const result = await handleNewAssignmentOutForDeliveryEvent(input, operatorName);
            if (result) {
                get().setNeedRefresh(true);
            }
            console.log('handleNewAssignmentOutForDelivery result:', result);
            return result || false;
        },
        handleTransferInOutForDelivery: async (input: OutForDeliveryInput): Promise<boolean> => {
            const result = await handleTransferInOutForDeliveryEvent(input);
            if (result) {
                get().setNeedRefresh(true);
            }
            console.log('handleTransferInOutForDelivery result:', result);
            return result;
        },
        handleSubmitWhsReview: async (operatorName?: string | null): Promise<boolean> => {
            const result = await handleSubmitWhsReview(operatorName);
            if (result) {
                get().setNeedRefresh(true);
            }
            console.log('handleSubmitWhsReview result:', result);
            return result;
        },
        setFromMapMode: (fromMapMode: boolean) => {
            set({ fromMapMode });
        },
        setTempScanPhotos: (photos: Map<string, string>) => {
            set({ tempScanPhotos: photos });
            console.log('📦 [DeliveryStore] 临时存储扫描图片:', {
                数量: photos.size,
                追踪号列表: Array.from(photos.keys())
            });
        },
        getTempScanPhotos: () => {
            return get().tempScanPhotos;
        },
        clearTempScanPhotos: () => {
            const size = get().tempScanPhotos.size;
            set({ tempScanPhotos: new Map() });
            console.log('🗑️ [DeliveryStore] 清空临时扫描图片:', size, '张');
        },
    }))
);

export const useDeliveryTaskOrderList = () => {
    const deliveryTaskOrderList = useDeliveryStore(state => state.deliveryTaskOrderList);
    const podAbnormalOrderList = useDeliveryStore(state => state.podAbnormalOrderList);
    const fetchDeliveryTaskOrderList = useDeliveryStore(state => state.fetchDeliveryTaskOrderList);
    const refreshDeliveryTaskOrderList = useDeliveryStore(state => state.refreshDeliveryTaskOrderList);
    const loading = useDeliveryStore(state => state.loading);
    
    useEffect(() => {
        fetchDeliveryTaskOrderList();
    }, [fetchDeliveryTaskOrderList]);
    return { deliveryTaskOrderList, podAbnormalOrderList, loading, refreshDeliveryTaskOrderList};
};

export const useNewDeliveryTaskOrderList = () => {
    const [deliveryTaskOrderList, setDeliveryTaskOrderList] = useState<DeliveryTaskOrderType[]>([]);
    const [podAbnormalOrderList, setPodAbnormalOrderList] = useState<DeliveryTaskOrderType[]>([]);
    const [loading, setLoading] = useState(false);
    const [version, setVersion] = useState(0);
    const [isInitialized, setIsInitialized] = useState(false);

    const fetchDeliveryTaskOrderList = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await client.query<NewDeliveryTaskOrderListResponse>({
                query: GET_NEW_DELIVERY_TASK_ORDER_LIST,
                variables: {
                    maxDeliveryTaskId: version,
                },
            });
            const maxDeliveryTaskId = data?.getNewDeliveryTaskOrderList?.maxDeliveryTaskId || 0;
            
            if (maxDeliveryTaskId != version || !isInitialized) {
                setVersion(maxDeliveryTaskId);
                setDeliveryTaskOrderList(data?.getNewDeliveryTaskOrderList?.deliveryTaskOrderList || []);
                setPodAbnormalOrderList(data?.getNewDeliveryTaskOrderList?.podAbnormalOrderList || []);
                setIsInitialized(true);
            }
        } catch (error) {
            console.error('Error fetching new delivery task order list:', error);
        } finally {
            setLoading(false);
        }
    }, [version, isInitialized]);

    const refreshDeliveryTaskOrderList = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await client.query<NewDeliveryTaskOrderListResponse>({
                query: GET_NEW_DELIVERY_TASK_ORDER_LIST,
                variables: {
                    maxDeliveryTaskId: 0, // 刷新时使用0获取所有数据
                },
            });
            const maxDeliveryTaskId = data?.getNewDeliveryTaskOrderList?.maxDeliveryTaskId || 0;
            setVersion(maxDeliveryTaskId);
            setDeliveryTaskOrderList(data?.getNewDeliveryTaskOrderList?.deliveryTaskOrderList || []);
            setPodAbnormalOrderList(data?.getNewDeliveryTaskOrderList?.podAbnormalOrderList || []);
            setIsInitialized(true);
        } catch (error) {
            console.error('Error refreshing new delivery task order list:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!isInitialized) {
            fetchDeliveryTaskOrderList();
        }
    }, [fetchDeliveryTaskOrderList, isInitialized]);

    return { deliveryTaskOrderList, podAbnormalOrderList, loading, refreshDeliveryTaskOrderList };
};

export const useDeliveryTaskOrder = (deliveryTaskOrderId: number) => {
    const { deliveryTaskOrderList, podAbnormalOrderList, loading } = useDeliveryTaskOrderList();
    return {
        deliveryTaskOrder: deliveryTaskOrderList.find(item => item.id == deliveryTaskOrderId),
        podTaskOrder: podAbnormalOrderList.find(item => item.id === deliveryTaskOrderId),
        loading,
    };
};

// 批量获取订单的 hook
export const useDeliveryTaskOrders = (deliveryTaskOrderIdList: number[]) => {
    const { deliveryTaskOrderList, podAbnormalOrderList, loading } = useDeliveryTaskOrderList();
    
    return {
        deliveryTaskOrders: deliveryTaskOrderList.filter(item => deliveryTaskOrderIdList.includes(Number(item.id))),
        podTaskOrders: podAbnormalOrderList.filter(item => deliveryTaskOrderIdList.includes(item.id)),
        loading,
    };
};

export const useDeliveryTaskOrderOperations = () : DeliveryTaskOrderOperations => {
    const { handleDelivered, handleNewAssignmentOutForDelivery, handleDeliveryFailed, handleNewAssignmentScan, handleTransferInScan, handleTransferInOutForDelivery, handleSubmitWhsReview} = useDeliveryStore(state => ({
        handleDelivered: state.handleDelivered,
        handleNewAssignmentOutForDelivery: state.handleNewAssignmentOutForDelivery,
        handleDeliveryFailed: state.handleDeliveryFailed,
        handleNewAssignmentScan: state.handleNewAssignmentScan,
        handleTransferInScan: state.handleTransferInScan,
        handleTransferInOutForDelivery: state.handleTransferInOutForDelivery,
        handleSubmitWhsReview: state.handleSubmitWhsReview,
    }));
    return { handleDelivered, handleNewAssignmentOutForDelivery, handleDeliveryFailed, handleNewAssignmentScan, handleTransferInScan, handleTransferInOutForDelivery,handleSubmitWhsReview};
};

export const useExceptionDictionaries = () => {
    const { exceptionDictionaries, fetchExceptionDictionaries } = useDeliveryStore(state => ({
        exceptionDictionaries: state.exceptionDictionaries,
        fetchExceptionDictionaries: state.fetchExceptionDictionaries,
    }));
    useEffect(() => {
        fetchExceptionDictionaries();
    }, [fetchExceptionDictionaries]);
    return exceptionDictionaries;
};

export const useDeliveryMethodI18nConfig = () => {
    const { deliveryMethodI18nConfig, fetchDeliveryMethodI18nConfig } = useDeliveryStore(state => ({
        deliveryMethodI18nConfig: state.deliveryMethodI18nConfig,
        fetchDeliveryMethodI18nConfig: state.fetchDeliveryMethodI18nConfig,
    }));
    useEffect(() => {
        fetchDeliveryMethodI18nConfig();
    }, [fetchDeliveryMethodI18nConfig]);
    return deliveryMethodI18nConfig;
};

export const useFromMapMode = () => {
    const { fromMapMode, setFromMapMode } = useDeliveryStore(state => ({
        fromMapMode: state.fromMapMode,
        setFromMapMode: state.setFromMapMode,
    }));
    return { fromMapMode, setFromMapMode };
};

export const useRouteColorMap = () => {
    const { routeColorMap, getOrAssignRouteColor } = useDeliveryStore(state => ({
        routeColorMap: state.routeColorMap,
        getOrAssignRouteColor: state.getOrAssignRouteColor,
    }));
    return { routeColorMap, getOrAssignRouteColor };
};

export const useRetryBlockControl = () => {
    const { setRetryBlocked, isRetryBlocked } = useDeliveryStore(state => ({
        setRetryBlocked: state.setRetryBlocked,
        isRetryBlocked: state.isRetryBlocked,
    }));
    return { setRetryBlocked, isRetryBlocked };
};

export const useTempScanPhotos = () => {
    const { tempScanPhotos, setTempScanPhotos, getTempScanPhotos, clearTempScanPhotos } = useDeliveryStore(state => ({
        tempScanPhotos: state.tempScanPhotos,
        setTempScanPhotos: state.setTempScanPhotos,
        getTempScanPhotos: state.getTempScanPhotos,
        clearTempScanPhotos: state.clearTempScanPhotos,
    }));
    return { tempScanPhotos, setTempScanPhotos, getTempScanPhotos, clearTempScanPhotos };
};

export const preload = async () => {
    const { fetchExceptionDictionaries, fetchDeliveryMethodI18nConfig } = useDeliveryStore.getState();
    await Promise.all([fetchExceptionDictionaries(), fetchDeliveryMethodI18nConfig()]);
};